import { useMemo, useState } from 'react'

import {
  alpha,
  Chip,
  Grid,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { useIntl } from 'react-intl'

import { SparckRewardsListFilterInfo } from '..'
import { VirginExperiencesRewardCard } from 'components'
import { WishType } from 'enums/wish'
import { t } from 'i18n'
import { getUserWishesByWishType } from 'utils/wishlist'

type Props = {
  sparckState?: any
  rewardsVirginExperiences: any
  onSelectReward(reward: any): void
}

export default function SparckVirginExperiencesRewardsList({
  sparckState,
  rewardsVirginExperiences,
  onSelectReward,
}: Props) {
  const theme = useTheme()

  const [searchText, setSearchText] = useState('')
  const [favoritesOnly, setFavoritesOnly] = useState(false)

  const intl = useIntl()

  const favoritesOnlyChips = [
    {
      id: 'allRewards',
      label: t('sparcks.sparck.allRewards'),
      onClick: () => setFavoritesOnly(false),
      isSelected: !favoritesOnly,
    },
    {
      id: 'favoritesOnly',
      label: t('sparcks.sparck.favoritesOnly'),
      onClick: () => setFavoritesOnly(true),
      isSelected: !!favoritesOnly,
    },
  ]

  const individualToRecognizeRetailerWishesIds = useMemo(
    () =>
      getUserWishesByWishType(
        sparckState?.individual_to_recognize,
        WishType.Retailer,
      )?.map(wish => +wish.wishable_id),

    [sparckState?.individual_to_recognize],
  )

  const filteredRewards = useMemo(() => {
    let newRewards = rewardsVirginExperiences

    if (favoritesOnly && individualToRecognizeRetailerWishesIds?.length) {
      newRewards = newRewards.filter(reward =>
        individualToRecognizeRetailerWishesIds.includes(reward.id),
      )
    }

    if (searchText) {
      newRewards = newRewards.filter(reward =>
        reward?.name?.toLowerCase()?.includes(searchText.toLowerCase()),
      )
    }

    return newRewards
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewardsVirginExperiences, searchText, favoritesOnly])

  return (
    <Grid container justifyContent='center' spacing={2}>
      <Grid item xs={12}>
        <Grid container spacing={2} alignItems='center' sx={{ mb: 3 }}>
          <Grid item xs={12} sm>
            <TextField
              id='rewardsSearch'
              variant='outlined'
              value={searchText}
              onChange={(event: any) => setSearchText(event.target.value)}
              label={t('sparcks.sparck.rewardsSearch')}
              placeholder={intl.formatMessage({
                id: 'sparcks.sparck.searchForAReward',
              })}
              fullWidth
            />
          </Grid>

          <SparckRewardsListFilterInfo
            maxAmount={
              sparckState?.event?.maximum_reward_amount_for_each_recognition
            }
          />

          {individualToRecognizeRetailerWishesIds?.length > 0 && (
            <Grid item xs={12} sm='auto'>
              {favoritesOnlyChips.map(chip => (
                <Chip
                  key={chip.id}
                  onClick={chip.onClick}
                  label={chip.label}
                  sx={{
                    mb: 0,
                    ...(chip.isSelected && {
                      outline: `1.5px solid ${theme.palette.primary.main}`,
                      bgcolor: alpha(theme.palette.primary.main, 0.15),
                    }),
                  }}
                />
              ))}
            </Grid>
          )}
        </Grid>
      </Grid>

      {filteredRewards?.length ? (
        filteredRewards.map(reward => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={reward.id}>
            <VirginExperiencesRewardCard
              reward={reward}
              onClick={() => onSelectReward && onSelectReward(reward)}
            />
          </Grid>
        ))
      ) : (
        <Grid item xs={12} sx={{ my: 3 }}>
          <Typography variant='body1' align='center' color='text.secondary'>
            {t('sparcks.sparck.noRewardsFound')}
          </Typography>
        </Grid>
      )}
    </Grid>
  )
}
